import features from '../../config/features.json';

export interface ToolDefinition {
  slug: string;
  name: string;
  description: string;
  tier: 'Free' | 'Standard' | 'Premium';
  category: 'Find Out Where You Stand' | 'See If You\'re On Track' | 'Stress-Test Your Plan';
  status: 'live' | 'coming-soon' | 'projection-feature';
  icon: string;
}

/**
 * Every tool the product knows about, with the status its code has reached.
 * What the hub and the router actually use is `tools`, below: the catalogue
 * with the build's feature flags applied.
 */
export const toolCatalogue: ToolDefinition[] = [
  // Getting Started (Discover tier)
  {
    slug: 'compounding',
    name: 'Compounding Visualiser',
    description: 'See how your investments grow over time with the power of compound returns.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F4C8}',
  },
  {
    slug: 'salary-sacrifice',
    name: 'Salary Sacrifice Calculator',
    description: 'Calculate the true cost of pension contributions after tax and NI savings.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F4B0}',
  },
  {
    slug: 'employer-match',
    name: 'Employer Match Maximiser',
    description: 'Find out how much free money you\u2019re leaving on the table.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F91D}',
  },
  {
    slug: 'volatility',
    name: 'Volatility Education',
    description: 'Learn why market dips can actually benefit long-term investors.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F30A}',
  },
  {
    slug: 'coast-fire',
    name: 'Coast FIRE Calculator',
    description: 'Find out when you can stop contributing and still retire on target.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F3D6}\uFE0F',
  },
  {
    slug: 'ni-top-up',
    name: 'NI Top-Up',
    description: 'See whether filling gaps in your National Insurance record pays for itself in extra State Pension.',
    tier: 'Free',
    category: 'Find Out Where You Stand',
    status: 'live',
    icon: '\u{1F9FE}',
  },

  {
    slug: 'earliest-retirement',
    name: 'Earliest Retirement Calculator',
    description: 'Find the earliest age you could retire and still have your money last, with savings bridging the years before your pension unlocks.',
    tier: 'Free',
    category: "See If You're On Track",
    status: 'live',
    icon: '\u{1F3C1}',
  },

  // Planning (Plan tier)
  {
    slug: 'retirement-mot',
    name: 'Retirement MOT',
    description: 'A quick health check: are you on track for the retirement you want?',
    tier: 'Standard',
    category: "See If You're On Track",
    status: 'live',
    icon: '\u{1F527}',
  },
  {
    slug: 'milestones',
    name: 'Age-Based Milestones',
    description: 'See how your savings compare to recommended benchmarks for your age.',
    tier: 'Standard',
    category: "See If You're On Track",
    status: 'coming-soon',
    icon: '\u{1F3AF}',
  },
  {
    slug: 'lisa',
    name: 'LISA Integration',
    description: 'Model the 25% government bonus, the early withdrawal penalty, and compare LISA vs SIPP for retirement saving.',
    tier: 'Standard',
    category: "See If You're On Track",
    status: 'coming-soon',
    icon: '\u{1F3E6}',
  },
  {
    slug: 'consolidation',
    name: 'Pension Consolidation',
    description: 'Guidance on tracing and consolidating lost or forgotten pension pots.',
    tier: 'Standard',
    category: "See If You're On Track",
    status: 'coming-soon',
    icon: '\u{1F4E6}',
  },

  // Advanced Analysis (Analyse tier)
  {
    slug: 'pension-gap',
    name: 'Pre-State Pension Gap',
    description: 'See the cost of retiring before your State Pension kicks in.',
    tier: 'Premium',
    category: 'Stress-Test Your Plan',
    status: 'live',
    icon: '\u{1F4CA}',
  },
  {
    slug: 'health-score',
    name: 'Financial Health Score',
    description: 'A holistic 0\u2013100 assessment across five financial dimensions.',
    tier: 'Premium',
    category: 'Stress-Test Your Plan',
    status: 'coming-soon',
    icon: '\u{1F9E0}',
  },
  {
    slug: 'historical-replay',
    name: 'Historical Market Replay',
    description: 'Run your plan against 100 years of market data. Available in projection view.',
    tier: 'Premium',
    category: 'Stress-Test Your Plan',
    status: 'projection-feature',
    icon: '\u{1F4DC}',
  },
  {
    slug: 'monte-carlo',
    name: 'Monte Carlo Analysis',
    description: 'Stress-test your plan against thousands of randomised scenarios. Available in projection view.',
    tier: 'Premium',
    category: 'Stress-Test Your Plan',
    status: 'projection-feature',
    icon: '\u{1F3B2}',
  },
  {
    slug: 'what-if',
    name: 'What-If Scenarios',
    description: 'Model alternative futures and compare each against your base plan. Available in projection view.',
    tier: 'Premium',
    category: 'Stress-Test Your Plan',
    status: 'projection-feature',
    icon: '\u{1F52E}',
  },
];

export const categories = ['Find Out Where You Stand', "See If You're On Track", 'Stress-Test Your Plan'] as const;

/**
 * Apply build-time flags to the catalogue. A flag can only take a tool away:
 * a live tool whose flag is off (or missing) is shown as coming soon, while a
 * tool that is not built yet stays coming soon whatever its flag says, and a
 * plan feature is untouched because it has no page of its own to switch off.
 */
export function applyToolFlags(
  catalogue: readonly ToolDefinition[],
  flags: Readonly<Record<string, boolean>>,
): ToolDefinition[] {
  return catalogue.map((tool) =>
    tool.status === 'live' && flags[tool.slug] !== true
      ? { ...tool, status: 'coming-soon' }
      : { ...tool },
  );
}

/** The tools this build ships: the catalogue with `features.json` applied. */
export const tools: ToolDefinition[] = applyToolFlags(toolCatalogue, features.tools);

export function getToolBySlug(slug: string): ToolDefinition | undefined {
  return tools.find((t) => t.slug === slug);
}
